import React, { useMemo, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Select, TextField, Typography } from "@coolremote/shared";
import { useStoreActions } from "../../models/RootStore";
import { useSetModbusUnitRegisterValue } from "@hooks/useModbusDevicesApi";
import {
  IModbusDeviceRow,
  UI_TYPE_BINARY,
  UI_TYPE_ENUMERATION,
  UI_TYPE_REVERSE_BINARY,
} from "../../screens/Modbus/types";
import { engUnitLabel } from "../../screens/Modbus/RegisterTable";
import styles from "./EditParamDialog.module.css";

interface IProps {
  /** A paramsTable row from ModbusUnitState/UnitStats.tsx — carries `rawRow`, the matching IModbusDeviceRow. */
  row: { code: string; name: string; value: any; measurementUnits?: string; rawRow?: IModbusDeviceRow };
  modbusUnitId: string;
  onClose: () => void;
  /** Called after a successful write, so the caller can refetch stats. */
  onSaved: () => void;
}

/**
 * Modbus counterpart to BacnetUnitState/EditParamPopUp. Rewritten (rather than
 * reused) because that component matches rows via unitParamTemplate.data
 * keyed by shortId and writes via useSetBacnetObjectValue — modbus rows have
 * no shortId/unitParamTemplate and are written via register type+address.
 * The layout mirrors the BACnet dialog; see EditParamDialog.module.css.
 */
const EditParamDialog: React.FC<IProps> = ({ row, modbusUnitId, onClose, onSaved }) => {
  const rawRow = row?.rawRow;
  const { addMessage } = useStoreActions((action: any) => action.errorMessage);

  // Success/failure must come from the callbacks, not the resolved value:
  // /set-register-value replies with a bare `{...dmResponse}` (no `data`
  // envelope like the other modbus endpoints), so the sdk unwraps it to
  // `undefined` and `mutate` resolves undefined whether it worked or not.
  const { mutate: setRegisterValue, isLoading: saving } = useSetModbusUnitRegisterValue({
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e: any) => addMessage({ message: e?.message || t`Failed to update value` }),
  });

  const isBinary = rawRow?.uiType === UI_TYPE_BINARY || rawRow?.uiType === UI_TYPE_REVERSE_BINARY;
  const isEnum = rawRow?.uiType === UI_TYPE_ENUMERATION;

  const currentRaw = Number(row?.value);
  const [newValue, setNewValue] = useState<number>(!isNaN(currentRaw) ? currentRaw : 0);

  // For reverse-binary rows, raw 0 means "on" — mirrors ModbusUIRenderer/ModbusUnitControl.
  const binaryOptions = useMemo(() => {
    if (!isBinary) return [];
    const onValue = rawRow?.uiType === UI_TYPE_REVERSE_BINARY ? 0 : 1;
    const offValue = onValue === 1 ? 0 : 1;
    return [
      { value: onValue, label: rawRow?.customEnum?.["1"] ?? t`On` },
      { value: offValue, label: rawRow?.customEnum?.["0"] ?? t`Off` },
    ];
  }, [isBinary, rawRow]);

  const enumOptions = useMemo(() => {
    if (!isEnum) return [];
    return Object.entries(rawRow?.customEnum || {}).map(([key, label]) => ({
      value: Number(key),
      label: label as string,
    }));
  }, [isEnum, rawRow]);

  // Fall back to the row's own units if the caller didn't precompute a symbol.
  const unitSymbol = row?.measurementUnits || engUnitLabel(rawRow?.units);

  // The current value reads better as its mapped label for binary/enum rows,
  // matching how the BACnet dialog resolves customEnum before display.
  const currentDisplay = useMemo(() => {
    if (isBinary || isEnum) {
      const opts = isBinary ? binaryOptions : enumOptions;
      const match = opts.find((o) => o.value === currentRaw);
      if (match) return match.label;
    }
    return row?.value;
  }, [isBinary, isEnum, binaryOptions, enumOptions, currentRaw, row]);

  const handleSave = () => {
    if (!rawRow || rawRow.registerAddress == null) {
      onClose();
      return;
    }
    setRegisterValue({
      modbusUnitId,
      data: {
        register: { type: rawRow.registerType, address: rawRow.registerAddress, ...(rawRow.bitNumber != null ? { bitNumber: rawRow.bitNumber } : {}) },
        value: newValue,
      },
    });
  };

  return (
    <BaseDialog
      open
      onClose={onClose}
      onConfirm={handleSave}
      title={row?.name}
      confirmLabel={t`Update`}
      cancelLabel={t`Cancel`}
      size="medium"
      loading={saving}
    >
      <Typography className={styles.valueTitle}>{t`Current value`}</Typography>
      <div className={styles.oldValueBox}>
        {currentDisplay}
        {/* Only value-type rows carry a unit; binary/enum show a mapped label. */}
        {unitSymbol && !isBinary && !isEnum ? <span>{unitSymbol}</span> : null}
      </div>

      <Typography className={styles.valueTitle}>{t`Set value`}</Typography>
      {isBinary || isEnum ? (
        <Select
          fullWidth
          menuPortal
          value={newValue}
          options={isBinary ? binaryOptions : enumOptions}
          onChange={(v: any) => setNewValue(Number(v))}
        />
      ) : (
        <TextField
          type="number"
          fullWidth
          value={newValue}
          min={rawRow?.minValue ?? undefined}
          max={rawRow?.maxValue ?? undefined}
          step={rawRow?.step ?? undefined}
          onChange={(v: any) => setNewValue(Number(v))}
        />
      )}
    </BaseDialog>
  );
};

export default EditParamDialog;
